import {
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  collection,
} from 'firebase/firestore';
import { db } from '../firebase/config/firebase';
import { bookingsCol, studentsCol, tripsCol } from '../firebase/firestore/collections';
import {
  Booking,
  Trip,
  StudentProfile,
  TripSeat,
  PaymentMethod,
  BookingStatus,
  PaymentStatus,
  StudentTripStats,
  TripDirection,
  PaymentAttempt,
} from '../types';
import { SEED_TRIPS } from '../constants/mockSeed';
import { generateSeatsForCapacity } from '../constants/seatLayout';
import { auditService } from './auditService';
import { extractStationFromRoute } from '../constants/tripRoutes';
import { treasuryService, TransactionVault } from './treasuryService';
import { invalidateOccupiedSeatsCache, isReturnTrip } from './tripService';

export function mapPaymentMethodToVault(method?: string): TransactionVault {
  if (!method) return 'CASH_BRANCH';
  const m = method.toUpperCase();
  if (m === 'INSTAPAY') return 'INSTAPAY';
  if (m === 'WALLET' || m === 'VODAFONE_CASH') return 'VODAFONE_CASH';
  if (m === 'ORANGE_CASH') return 'ORANGE_CASH';
  if (m === 'ETISALAT_CASH') return 'ETISALAT_CASH';
  if (m === 'WE_PAY') return 'WE_PAY';
  if (m === 'BANK_ACCOUNT' || m === 'CARD') return 'INSTAPAY';
  return 'CASH_BRANCH';
}

export interface AdminBookTripInput {
  student: StudentProfile;
  trip: Trip;
  seatNumber: number;
  paymentMethod: PaymentMethod;
  isPaid: boolean;
  direction?: TripDirection;
  adminEmail?: string;
  adminNote?: string;
  bookingGroupId?: string;
  bookingGroupCode?: string;
  skipPaymentAttemptAndTreasury?: boolean;
}

export interface AdminTransferPassengerInput {
  bookingId: string;
  oldTripId: string;
  oldSeatNumber: number;
  newTrip: Trip;
  newSeatNumber: number;
  adminEmail?: string;
  reason?: string;
}

export interface AdminChangeSeatInput {
  bookingId: string;
  tripId: string;
  oldSeatNumber: number;
  newSeatNumber: number;
  adminEmail?: string;
}

export interface AdminPostponeBookingInput {
  bookingId: string;
  tripId: string;
  seatNumber: number;
  adminEmail?: string;
  postponeNotes?: string;
}

export interface PassengerManifestItem {
  seatNumber: number;
  booking?: Booking;
  studentName?: string;
  studentPhone?: string;
  studentEmail?: string;
  studentUniversity?: string;
  bookingCode?: string;
  ticketId?: string;
  paymentStatus?: string;
  paymentMethod?: string;
  price?: number;
  isBooked: boolean;
  status?: string;
}

// In-memory cache for blazing fast admin responsiveness
let cachedBookings: { data: Booking[]; timestamp: number } | null = null;
let cachedStudents: { data: StudentProfile[]; timestamp: number } | null = null;
let cachedTrips: { data: Trip[]; timestamp: number } | null = null;

export const invalidateAdminBookingsCache = () => {
  cachedBookings = null;
};
export const invalidateAdminStudentsCache = () => {
  cachedStudents = null;
};
export const invalidateAdminTripsCache = () => {
  cachedTrips = null;
};

export const adminBookingService = {
  invalidateBookingsCache: invalidateAdminBookingsCache,
  invalidateStudentsCache: invalidateAdminStudentsCache,
  invalidateTripsCache: invalidateAdminTripsCache,

  /**
   * 1. Load all bookings (combines Firestore & dev session) with instant memory caching - unlimited records
   */
  async getAllBookings(forceRefresh = false): Promise<Booking[]> {
    if (!forceRefresh && cachedBookings && Date.now() - cachedBookings.timestamp < 15000) {
      return cachedBookings.data;
    }

    let list: Booking[] = [];
    try {
      const snap = await getDocs(bookingsCol);
      if (!snap.empty) {
        list = snap.docs.map((d) => ({ ...d.data(), id: d.id })) as Booking[];
        list.sort((a, b) => {
          const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return timeB - timeA;
        });
      }
    } catch (err) {
      console.warn('Error loading bookings directly, attempting query:', err);
      try {
        const q = query(bookingsCol, orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        if (!snap.empty) {
          list = snap.docs.map((d) => ({ ...d.data(), id: d.id })) as Booking[];
        }
      } catch (innerErr) {
        console.warn('Fallback bookings fetch failed:', innerErr);
      }
    }

    // Merge dev storage
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem('nashgo_dev_student_bookings') || localStorage.getItem('nashgo_dev_student_bookings');
        if (raw) {
          const devList = JSON.parse(raw) as Booking[];
          const devOnly = devList.filter((b) => !list.some((existing) => existing.id === b.id));
          list = [...devOnly, ...list];
        }
      } catch (_) {}
    }

    cachedBookings = { data: list, timestamp: Date.now() };
    return list;
  },

  /**
   * 2. Load all students with instant memory caching - unlimited records, none deleted
   */
  async getAllStudents(forceRefresh = false): Promise<StudentProfile[]> {
    if (!forceRefresh && cachedStudents && Date.now() - cachedStudents.timestamp < 20000) {
      return cachedStudents.data;
    }

    try {
      // 1. Fetch all student profiles directly without any artificial limit
      const snap = await getDocs(studentsCol);
      if (!snap.empty) {
        const list = snap.docs.map((d) => ({ ...d.data(), uid: d.id })) as StudentProfile[];
        // Sort in memory by createdAt descending (safely handles any date format or missing field)
        list.sort((a, b) => {
          const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return timeB - timeA;
        });
        cachedStudents = { data: list, timestamp: Date.now() };
        return list;
      }
    } catch (err) {
      console.warn('Could not load students directly, attempting ordered query:', err);
      try {
        const q = query(studentsCol, orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const list = snap.docs.map((d) => ({ ...d.data(), uid: d.id })) as StudentProfile[];
          cachedStudents = { data: list, timestamp: Date.now() };
          return list;
        }
      } catch (innerErr) {
        console.warn('Fallback students fetch failed:', innerErr);
      }
    }

    return cachedStudents?.data || [];
  },

  /**
   * 2.1 Calculate trip statistics for a list of bookings
   */
  calculateStudentTripStats(bookings: Booking[]): StudentTripStats {
    const today = new Date().toISOString().split('T')[0];
    const validBookings = bookings.filter(
      (b) =>
        b.status !== 'CANCELLED' &&
        b.status !== 'DELETED' &&
        (b.bookingStatus as string) !== 'CANCELLED' &&
        (b.bookingStatus as string) !== 'DELETED' &&
        b.paymentStatus !== 'REJECTED' &&
        b.paymentStatus !== 'CANCELLED'
    );

    const totalBooked = validBookings.length;
    const remaining = validBookings.filter(
      (b) => (b.tripDate >= today && b.status !== 'COMPLETED' && (b.bookingStatus as string) !== 'COMPLETED')
    ).length;
    const completed = validBookings.filter(
      (b) => (b.tripDate < today || b.status === 'COMPLETED' || (b.bookingStatus as string) === 'COMPLETED')
    ).length;

    return {
      totalBooked,
      remaining,
      completed,
      isMultiTripSubscriber: totalBooked > 1,
      activeBookingCodes: validBookings.map((b) => b.bookingCode),
    };
  },

  /**
   * 2.2 Get trip stats for a specific student
   */
  async getStudentTripStats(studentUid: string, allBookingsList?: Booking[]): Promise<StudentTripStats> {
    const bookings = allBookingsList || (await this.getAllBookings());
    const studentBookings = bookings.filter((b) => b.studentId === studentUid);
    return this.calculateStudentTripStats(studentBookings);
  },

  /**
   * 2.3 Synchronize student assigned route to their bookings and demand records
   */
  async syncStudentRouteToBookingsAndDemand(
    studentUid: string,
    assignedRoute: string,
    pickupStation?: string,
    adminEmail?: string
  ): Promise<{ updatedBookingsCount: number; updatedDemandCount: number }> {
    const cleanRoute = assignedRoute?.trim() || '';
    const cleanStation = pickupStation?.trim() || extractStationFromRoute(cleanRoute);
    let updatedBookingsCount = 0;
    let updatedDemandCount = 0;

    // 1. Update Student Profile in Firestore
    try {
      const studentDocRef = doc(db, 'students', studentUid);
      await updateDoc(studentDocRef, {
        assignedRoute: cleanRoute,
        defaultPickupStation: cleanStation,
        updatedAt: new Date().toISOString(),
      });
    } catch (sErr) {
      console.warn('Could not update student doc directly:', sErr);
    }

    // 2. Update all bookings for this student
    try {
      const allBookings = await this.getAllBookings(true);
      const studentBookings = allBookings.filter((b) => b.studentId === studentUid);

      for (const b of studentBookings) {
        try {
          const bookingDocRef = doc(db, 'bookings', b.id);
          const updatePayload: Record<string, any> = {
            'studentSnapshot.assignedRoute': cleanRoute,
            assignedRoute: cleanRoute,
            updatedAt: new Date().toISOString(),
          };
          if (cleanStation) {
            updatePayload.pickupStationName = cleanStation;
            updatePayload.origin = cleanStation;
          }
          await updateDoc(bookingDocRef, updatePayload);
          updatedBookingsCount++;
        } catch (bErr) {
          console.warn(`Could not sync route to booking ${b.id}:`, bErr);
        }
      }
    } catch (allErr) {
      console.warn('Error syncing to bookings:', allErr);
    }

    // 3. Update student demand records
    try {
      const demandColRef = collection(db, 'studentDemand');
      const snap = await getDocs(demandColRef);
      if (!snap.empty) {
        for (const d of snap.docs) {
          const data = d.data();
          if (data.studentId === studentUid || data.createdBy?.uid === studentUid) {
            try {
              await updateDoc(doc(db, 'studentDemand', d.id), {
                assignedRoute: cleanRoute,
                pickupStation: cleanStation,
                updatedAt: new Date().toISOString(),
              });
              updatedDemandCount++;
            } catch {}
          }
        }
      }
    } catch (dErr) {
      console.warn('Error syncing route to studentDemand:', dErr);
    }

    // Invalidate caches so all components receive the fresh synced data
    invalidateAdminStudentsCache();
    invalidateAdminBookingsCache();

    // Audit log
    try {
      await auditService.logAction(
        'admin',
        adminEmail || 'admin@otostudents.com',
        'STUDENT_ROUTE_SYNC',
        'STUDENT',
        studentUid,
        {
          assignedRoute: cleanRoute,
          pickupStation: cleanStation,
          updatedBookingsCount,
          updatedDemandCount,
          description: `مزامنة خط السير المخصص للطالب (${cleanRoute}) مع كافة حجوزاته (${updatedBookingsCount} حجز) وطلباته`,
        }
      );
    } catch {}

    return { updatedBookingsCount, updatedDemandCount };
  },

  /**
   * 3. Load all trips with instant memory caching - unlimited trips
   */
  async getAllTrips(forceRefresh = false): Promise<Trip[]> {
    if (!forceRefresh && cachedTrips && Date.now() - cachedTrips.timestamp < 20000) {
      return cachedTrips.data;
    }

    let trips: Trip[] = [];
    let firestoreQueried = false;
    try {
      const snap = await getDocs(tripsCol);
      firestoreQueried = true;
      if (!snap.empty) {
        trips = snap.docs.map((d) => ({ ...d.data(), id: d.id })) as Trip[];
        trips.sort((a, b) => {
          const dateComp = (b.date || '').localeCompare(a.date || '');
          if (dateComp !== 0) return dateComp;
          return (a.departureTime || '').localeCompare(b.departureTime || '');
        });
      }
    } catch (err) {
      console.warn('Error fetching trips, falling back to seed:', err);
    }

    if (!firestoreQueried && trips.length === 0) {
      trips = SEED_TRIPS;
    }

    cachedTrips = { data: trips, timestamp: Date.now() };
    return trips;
  },

  /**
   * 3.1 Delete trip permanently from database and cache
   */
  async deleteTrip(tripId: string, adminEmail?: string): Promise<boolean> {
    try {
      const tripDocRef = doc(db, 'trips', tripId);
      await deleteDoc(tripDocRef);

      // Invalidate memory cache so all connected components and devices update
      invalidateAdminTripsCache();

      // Audit log
      try {
        await auditService.logAction(
          'admin',
          adminEmail || 'admin@otostudents.com',
          'TRIP_DELETE',
          'TRIP',
          tripId,
          {
            tripId,
            description: `حذف الرحلة نهائياً من قاعدة البيانات (معرف الرحلة: ${tripId})`,
          }
        );
      } catch {}

      return true;
    } catch (err) {
      console.error('Failed to delete trip:', err);
      throw err;
    }
  },

  /**
   * 3.1.1 Update trip details (Price, Timing, Driver, Route, Status)
   */
  async updateTrip(
    tripId: string,
    updates: Partial<Trip>,
    adminEmail?: string
  ): Promise<Trip> {
    try {
      const tripDocRef = doc(db, 'trips', tripId);
      const nowIso = new Date().toISOString();
      const updatedPayload: Partial<Trip> = {
        ...updates,
        updatedAt: nowIso,
      };

      await setDoc(tripDocRef, updatedPayload, { merge: true });

      invalidateAdminTripsCache();
      invalidateOccupiedSeatsCache(tripId);

      // Audit log
      try {
        await auditService.logAction(
          'admin',
          adminEmail || 'admin@otostudents.com',
          'TRIP_UPDATE_STATUS',
          'TRIP',
          tripId,
          {
            tripId,
            updates,
            description: `تعديل بيانات الرحلة (السعر: ${updates.price || '—'} ج.م، الموعد: ${updates.departureTime || '—'})`,
          }
        );
      } catch {}

      return {
        id: tripId,
        ...updatedPayload,
      } as Trip;
    } catch (err) {
      console.error('Failed to update trip:', err);
      throw err;
    }
  },

  /**
   * 3.2 Reconcile and audit all trip seat counters with authoritative bookings and seats
   * Fixes any desynchronization, negative seats, or counts exceeding bus capacity.
   */
  async reconcileAllTripSeats(): Promise<{
    scannedTrips: number;
    correctedTrips: number;
    details: Array<{ tripCode: string; date: string; beforeAvail: number; afterAvail: number; bookedCount: number }>;
  }> {
    const allTrips = await this.getAllTrips(true);
    const allBookings = await this.getAllBookings(true);

    // Map active bookings count per trip
    const activeBookingsMap = new Map<string, number>();
    allBookings.forEach((b) => {
      const isCancelled =
        b.status === 'CANCELLED' ||
        b.bookingStatus === 'CANCELLED' ||
        b.status === 'POSTPONED' ||
        b.bookingStatus === 'POSTPONED' ||
        b.status === 'DELETED' ||
        b.bookingStatus === 'DELETED' ||
        (b.paymentStatus as string) === 'REJECTED' ||
        (b.paymentStatus as string) === 'CANCELLED';

      if (!isCancelled && b.tripId) {
        activeBookingsMap.set(b.tripId, (activeBookingsMap.get(b.tripId) || 0) + 1);
      }
    });

    let correctedTrips = 0;
    const details: Array<{ tripCode: string; date: string; beforeAvail: number; afterAvail: number; bookedCount: number }> = [];

    for (const trip of allTrips) {
      const totalSeats = trip.totalSeats || trip.capacity || 50;
      const bookedCount = activeBookingsMap.get(trip.id) || 0;
      const correctAvailable = Math.max(0, totalSeats - bookedCount);
      const curAvailable = typeof trip.availableSeats === 'number' ? trip.availableSeats : totalSeats;

      if (curAvailable !== correctAvailable || trip.availableSeatsCount !== correctAvailable) {
        correctedTrips++;
        details.push({
          tripCode: trip.tripCode || trip.id,
          date: trip.date,
          beforeAvail: curAvailable,
          afterAvail: correctAvailable,
          bookedCount,
        });

        try {
          const tripRef = doc(db, 'trips', trip.id);
          await updateDoc(tripRef, {
            availableSeats: correctAvailable,
            availableSeatsCount: correctAvailable,
            totalSeats,
            capacity: totalSeats,
            status: correctAvailable === 0 ? 'FULL' : 'OPEN',
            updatedAt: new Date().toISOString(),
          });
        } catch (e) {
          console.warn(`Could not update reconciled trip ${trip.id}:`, e);
        }
      }
    }

    // Invalidate memory cache so changes reflect everywhere immediately
    invalidateAdminTripsCache();

    return {
      scannedTrips: allTrips.length,
      correctedTrips,
      details,
    };
  },

  /**
   * 4. Load full trip passenger manifest (seats + bookings)
   */
  async getTripPassengerManifest(trip: Trip): Promise<{
    seats: TripSeat[];
    passengers: PassengerManifestItem[];
    occupiedCount: number;
    availableCount: number;
  }> {
    const totalSeats = trip.totalSeats || trip.capacity || 50;
    let tripSeats: TripSeat[] = [];

    // 1. Fetch seat subcollection
    try {
      const seatsColRef = collection(db, 'trips', trip.id, 'seats');
      const snap = await getDocs(seatsColRef);
      if (!snap.empty) {
        tripSeats = snap.docs.map((d) => ({ ...d.data(), id: d.id })) as TripSeat[];
      }
    } catch (_) {}

    if (tripSeats.length === 0) {
      tripSeats = generateSeatsForCapacity(totalSeats).map((s) => ({
        id: String(s.seatNumber),
        seatNumber: s.seatNumber,
        tripId: trip.id,
        status: 'AVAILABLE',
        updatedAt: new Date().toISOString(),
      }));
    }

    // 2. Fetch all bookings for this trip
    const allBookings = await this.getAllBookings();
    const tripBookings = allBookings.filter(
      (b) =>
        b.tripId === trip.id &&
        (b.status || b.bookingStatus) !== 'CANCELLED' &&
        (b.status || b.bookingStatus) !== 'POSTPONED' &&
        (b.status || b.bookingStatus) !== 'DELETED' &&
        (b.paymentStatus as string) !== 'REJECTED' &&
        (b.paymentStatus as string) !== 'CANCELLED'
    );

    // 3. Synthesize passenger list
    const passengers: PassengerManifestItem[] = [];
    let occupiedCount = 0;

    for (let sNum = 1; sNum <= totalSeats; sNum++) {
      const booking = tripBookings.find((b) => Number(b.seatNumber) === sNum);
      const seatDoc = tripSeats.find((s) => Number(s.seatNumber) === sNum);
      const isOccupied = !!booking || seatDoc?.status === 'BOOKED' || seatDoc?.status === 'HELD';

      if (isOccupied) {
        occupiedCount++;
      }

      passengers.push({
        seatNumber: sNum,
        booking,
        studentName: booking?.studentSnapshot?.fullName || (isOccupied ? `مقعد محجوز (${sNum})` : undefined),
        studentPhone: booking?.studentSnapshot?.phoneNumber || undefined,
        studentEmail: booking?.studentSnapshot?.email || undefined,
        studentUniversity: booking?.universityName || trip.universityName,
        bookingCode: booking?.bookingCode || (isOccupied ? `RSV-${sNum}` : undefined),
        ticketId: booking?.ticketId || undefined,
        paymentStatus: booking?.paymentStatus || (isOccupied ? 'PAID' : undefined),
        paymentMethod: booking?.paymentMethod || undefined,
        price: booking?.price || trip.price,
        isBooked: isOccupied,
        status: booking?.status || booking?.bookingStatus || (isOccupied ? 'CONFIRMED' : 'AVAILABLE'),
      });
    }

    const availableCount = Math.max(0, totalSeats - occupiedCount);

    return {
      seats: tripSeats,
      passengers,
      occupiedCount,
      availableCount,
    };
  },

  /**
   * 5. Book trip for student directly (Admin Booking)
   */
  async bookTripForStudent(input: AdminBookTripInput): Promise<{ success: boolean; booking: Booking; message: string }> {
    const { student, trip, seatNumber, paymentMethod, isPaid, adminEmail, adminNote } = input;
    const nowIso = new Date().toISOString();

    // 1. Prevent duplicate booking for the same student on the same trip
    try {
      const existingQuery = query(
        bookingsCol,
        where('studentId', '==', student.uid),
        where('tripId', '==', trip.id)
      );
      const existingSnap = await getDocs(existingQuery);
      const activeDuplicate = existingSnap.docs.find((d) => {
        const data = d.data() as any;
        const st = data.status || data.bookingStatus;
        const pst = data.paymentStatus;
        return (
          st !== 'CANCELLED' &&
          st !== 'REJECTED' &&
          st !== 'DELETED' &&
          pst !== 'CANCELLED' &&
          pst !== 'REJECTED' &&
          pst !== 'DELETED'
        );
      });
      if (activeDuplicate) {
        const dupCode = activeDuplicate.data().bookingCode || activeDuplicate.id;
        throw new Error(
          `الطالب (${student.fullName}) مسجل بالفعل في هذه الرحلة مسبقاً (كود الحجز: ${dupCode})! لا يمكن حجز نفس الرحلة مرتين لنفس الطالب.`
        );
      }
    } catch (checkErr: any) {
      if (checkErr.message?.includes('مسجل بالفعل')) throw checkErr;
      console.warn('Duplicate check warning:', checkErr);
    }

    // 2. Prevent double-booking seat (concurrency check)
    const seatRef = doc(db, 'trips', trip.id, 'seats', String(seatNumber));
    try {
      const seatSnap = await getDoc(seatRef);
      if (
        seatSnap.exists() &&
        seatSnap.data()?.status === 'BOOKED' &&
        seatSnap.data()?.studentId &&
        seatSnap.data()?.studentId !== student.uid
      ) {
        throw new Error(`المقعد رقم (#${seatNumber}) تم حجزه للتو بواسطة طالب آخر! يرجى اختيار مقعد آخر.`);
      }
    } catch (seatErr: any) {
      if (seatErr.message?.includes('تم حجزه للتو')) throw seatErr;
      console.warn('Seat lock precheck warning:', seatErr);
    }

    const bookingId = `BK-${Date.now().toString(36).toUpperCase()}-${Math.floor(100 + Math.random() * 900)}`;
    const bookingCode = `OTO-${Math.floor(10000 + Math.random() * 90000)}`;
    const ticketId = `TCK-${Math.floor(10000 + Math.random() * 90000)}`;

    const paymentStatus: PaymentStatus = isPaid
      ? 'PAID'
      : (paymentMethod === 'BRANCH' ? 'AWAITING_BRANCH_PAYMENT' : 'PENDING_VERIFICATION');
    const bookingStatus: BookingStatus = isPaid ? 'CONFIRMED' : 'PENDING_PAYMENT_VERIFICATION';
    const attemptId = `ATT-${bookingCode.replace(/[^A-Z0-9]/g, '')}`;

    // Normalize direction
    const rawDir = input.direction || trip.direction;
    const isRet = isReturnTrip(rawDir);
    const direction: TripDirection = isRet ? 'RETURN' : 'OUTBOUND';

    // Proper origin and destination inversion for return trips
    const origin = isRet
      ? (trip.destination || trip.universityName || 'الجامعة')
      : (trip.origin || 'الإسكندرية');
    const destination = isRet
      ? (trip.origin || 'الإسكندرية')
      : (trip.destination || trip.universityName || 'الجامعة');

    const bookingDoc: Booking = {
      id: bookingId,
      bookingCode,
      ticketId,
      studentId: student.uid,
      tripId: trip.id,
      tripCode: trip.tripCode,
      universityId: trip.universityId,
      universityName: trip.universityName,
      tripDate: trip.date,
      departureTime: trip.departureTime,
      origin,
      destination,
      direction,
      seatNumber,
      vehiclePlate: trip.vehiclePlate || 'س ق د 1842',
      vehicleId: trip.vehicleId,
      price: trip.price,
      currency: 'EGP',
      status: bookingStatus,
      bookingStatus,
      paymentMethod,
      paymentStatus,
      paymentAttemptId: attemptId,
      ticketSecurityHash: `OTO_SEC_HASH_${bookingCode}_${Date.now()}`,
      adminNote: adminNote || `تم الحجز بواسطة المسؤول (${adminEmail || 'Admin'}) مباشرة`,
      studentSnapshot: {
        uid: student.uid,
        fullName: student.fullName,
        email: student.email,
        phoneNumber: student.phoneNumber,
        address: student.address,
        universityId: student.universityId || trip.universityId,
        universityName: student.universityName || trip.universityName,
      },
      bookingGroupId: input.bookingGroupId || undefined,
      bookingGroupCode: input.bookingGroupCode || undefined,
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    // 1. Write booking to Firestore
    try {
      await setDoc(doc(db, 'bookings', bookingId), bookingDoc);
    } catch (e) {
      console.warn('Firestore setDoc booking error, writing dev fallback:', e);
    }

    // 2. Lock Seat in trips/{tripId}/seats/{seatNumber}
    try {
      await setDoc(
        seatRef,
        {
          id: String(seatNumber),
          seatNumber,
          tripId: trip.id,
          status: 'BOOKED',
          bookingId,
          studentId: student.uid,
          updatedAt: nowIso,
        },
        { merge: true }
      );
    } catch (_) {}

    // 3. Decrement available seats on Trip
    try {
      const tripRef = doc(db, 'trips', trip.id);
      const tripSnap = await getDoc(tripRef);
      if (tripSnap.exists()) {
        const cur = tripSnap.data()?.availableSeats || trip.availableSeats || 14;
        await updateDoc(tripRef, {
          availableSeats: Math.max(0, cur - 1),
          availableSeatsCount: Math.max(0, cur - 1),
          updatedAt: nowIso,
        });
      }
    } catch (_) {}

    // Invalidate in-memory caches
    invalidateAdminBookingsCache();
    invalidateOccupiedSeatsCache(trip.id);

    // 4. Save to dev sessionStorage & localStorage
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem(`nashgo_dev_booking_${bookingId}`, JSON.stringify(bookingDoc));
        localStorage.setItem(`nashgo_dev_booking_${bookingId}`, JSON.stringify(bookingDoc));
        const raw = sessionStorage.getItem('nashgo_dev_student_bookings') || '[]';
        const list = JSON.parse(raw);
        list.unshift(bookingDoc);
        sessionStorage.setItem('nashgo_dev_student_bookings', JSON.stringify(list));
      } catch (_) {}
    }

    // 5. Audit log
    await auditService.logAction(
      student.uid,
      adminEmail || 'admin@otostudents.com',
      'TRIP_CREATED' as any,
      'TRIP' as any,
      bookingId,
      {
        action: 'ADMIN_DIRECT_BOOKING',
        studentName: student.fullName,
        studentPhone: student.phoneNumber,
        tripDate: trip.date,
        seatNumber,
        bookingCode,
      }
    );

    // 6. Payment Attempt and Treasury integration
    if (!input.skipPaymentAttemptAndTreasury) {
      const attemptDoc: PaymentAttempt = {
        id: attemptId,
        bookingId,
        bookingCode,
        studentId: student.uid,
        studentName: student.fullName,
        studentPhone: student.phoneNumber,
        universityName: student.universityName || trip.universityName,
        tripDate: trip.date,
        seatNumber,
        amount: trip.price,
        currency: 'EGP',
        paymentMethod,
        provider: 'MANUAL',
        status: paymentStatus,
        adminNote: adminNote || (isPaid ? 'تم التحصيل المباشر بواسطة الإدارة' : 'حجز إداري قيد السداد'),
        createdAt: nowIso,
        updatedAt: nowIso,
        expiresAt: new Date(Date.now() + 86400000).toISOString(),
      };

      if (isPaid) {
        attemptDoc.reviewedAt = nowIso;
        attemptDoc.reviewedBy = adminEmail || 'مدير النظام';
      }

      // 6.1 Write payment attempt to Firestore
      try {
        await setDoc(doc(db, 'paymentAttempts', attemptId), attemptDoc);
      } catch (err) {
        console.warn('Firestore setDoc paymentAttempt error:', err);
      }

      // 6.2 Cache in localStorage/sessionStorage for instant reflection in AdminPaymentVerification
      if (typeof window !== 'undefined') {
        try {
          const rawAttempts =
            localStorage.getItem('oto_dev_payment_attempts') ||
            sessionStorage.getItem('oto_dev_payment_attempts') ||
            '[]';
          const attemptsList: PaymentAttempt[] = JSON.parse(rawAttempts);
          const existingIdx = attemptsList.findIndex((a) => a.id === attemptId);
          if (existingIdx >= 0) {
            attemptsList[existingIdx] = attemptDoc;
          } else {
            attemptsList.unshift(attemptDoc);
          }
          localStorage.setItem('oto_dev_payment_attempts', JSON.stringify(attemptsList));
          sessionStorage.setItem('oto_dev_payment_attempts', JSON.stringify(attemptsList));
        } catch (_) {}
      }

      // 6.3 If "تم التحصيل" (isPaid === true), immediately record income in Treasury!
      if (isPaid && trip.price > 0) {
        try {
          const vault = mapPaymentMethodToVault(paymentMethod);
          await treasuryService.addTransaction({
            type: 'INCOME',
            category: 'BOOKING_PAYMENT',
            categoryLabelAr: 'تحصيل حجز رحلة طالب (إدارة)',
            amount: trip.price,
            vault,
            description: `تحصيل مباشر لحجز رحلة (${trip.origin || 'المحطة'} ➔ ${trip.destination || 'الجامعة'}) للطالب (${student.fullName}) - كود ${bookingCode}`,
            referenceNumber: bookingCode,
            recordedBy: adminEmail || 'مدير النظام (تحصيل مباشر)',
            date: nowIso,
            status: 'CONFIRMED',
          });
        } catch (tErr) {
          console.warn('Could not record direct booking in treasury:', tErr);
        }
      }
    }

    return {
      success: true,
      booking: bookingDoc,
      message: `تم حجز المقعد #${seatNumber} للطالب ${student.fullName} بنجاح. كود الحجز: ${bookingCode}`,
    };
  },

  /**
   * 6. Transfer passenger to another trip (Move to another date / time / trip)
   */
  async transferPassengerToTrip(input: AdminTransferPassengerInput): Promise<{ success: boolean; message: string }> {
    const { bookingId, oldTripId, oldSeatNumber, newTrip, newSeatNumber, adminEmail, reason } = input;
    const nowIso = new Date().toISOString();

    // 1. Release old seat
    try {
      const oldSeatRef = doc(db, 'trips', oldTripId, 'seats', String(oldSeatNumber));
      await updateDoc(oldSeatRef, {
        status: 'AVAILABLE',
        bookingId: null,
        studentId: null,
        updatedAt: nowIso,
      });
    } catch (_) {}

    // 2. Increment old trip availableSeats (capped at totalSeats)
    try {
      const oldTripRef = doc(db, 'trips', oldTripId);
      const snap = await getDoc(oldTripRef);
      if (snap.exists()) {
        const cur = snap.data()?.availableSeats || 0;
        const maxCap = snap.data()?.totalSeats || snap.data()?.capacity || 50;
        const newAvail = Math.min(maxCap, cur + 1);
        await updateDoc(oldTripRef, {
          availableSeats: newAvail,
          availableSeatsCount: newAvail,
          status: 'OPEN',
          updatedAt: nowIso,
        });
      }
    } catch (_) {}

    // 3. Book new seat
    try {
      const newSeatRef = doc(db, 'trips', newTrip.id, 'seats', String(newSeatNumber));
      await setDoc(
        newSeatRef,
        {
          seatNumber: newSeatNumber,
          tripId: newTrip.id,
          status: 'BOOKED',
          bookingId,
          updatedAt: nowIso,
        },
        { merge: true }
      );
    } catch (_) {}

    // 4. Decrement new trip availableSeats
    try {
      const newTripRef = doc(db, 'trips', newTrip.id);
      const snap = await getDoc(newTripRef);
      if (snap.exists()) {
        const cur = snap.data()?.availableSeats || newTrip.availableSeats || 14;
        await updateDoc(newTripRef, {
          availableSeats: Math.max(0, cur - 1),
          availableSeatsCount: Math.max(0, cur - 1),
          updatedAt: nowIso,
        });
      }
    } catch (_) {}

    // 5. Update booking document
    const updatePayload: Partial<Booking> = {
      tripId: newTrip.id,
      tripCode: newTrip.tripCode,
      tripDate: newTrip.date,
      departureTime: newTrip.departureTime,
      universityId: newTrip.universityId,
      universityName: newTrip.universityName,
      origin: newTrip.origin,
      destination: newTrip.destination,
      direction: newTrip.direction || 'OUTBOUND',
      seatNumber: newSeatNumber,
      status: 'CONFIRMED',
      bookingStatus: 'CONFIRMED',
      adminNote: `تم نقل الرحلة من (رحلة ${oldTripId} - مقعد ${oldSeatNumber}) إلى (رحلة ${newTrip.tripCode} - مقعد ${newSeatNumber}) بتاريخ ${nowIso}. ${reason ? `السبب: ${reason}` : ''}`,
      updatedAt: nowIso,
    };

    try {
      await updateDoc(doc(db, 'bookings', bookingId), updatePayload);
    } catch (e) {
      console.warn('Firestore updateDoc transfer error, falling back locally:', e);
    }

    // 6. Local storage sync
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem('nashgo_dev_student_bookings');
        if (raw) {
          const list = JSON.parse(raw) as Booking[];
          const updated = list.map((b) => (b.id === bookingId ? { ...b, ...updatePayload } : b));
          sessionStorage.setItem('nashgo_dev_student_bookings', JSON.stringify(updated));
        }
      } catch (_) {}
    }

    // 7. Audit log
    await auditService.logAction(
      adminEmail || 'admin',
      adminEmail || 'admin@otostudents.com',
      'TRIP_UPDATED' as any,
      'TRIP' as any,
      bookingId,
      {
        action: 'TRANSFER_PASSENGER',
        oldTripId,
        oldSeatNumber,
        newTripId: newTrip.id,
        newSeatNumber,
        newTripDate: newTrip.date,
        reason,
      }
    );

    return {
      success: true,
      message: `تم نقل الراكب بنجاح إلى رحلة ${newTrip.tripCode} (يوم ${newTrip.date} - مقعد #${newSeatNumber}).`,
    };
  },

  /**
   * 7. Change passenger seat within same trip
   */
  async changePassengerSeat(input: AdminChangeSeatInput): Promise<{ success: boolean; message: string }> {
    const { bookingId, tripId, oldSeatNumber, newSeatNumber, adminEmail } = input;
    const nowIso = new Date().toISOString();

    // Release old seat
    try {
      const oldSeatRef = doc(db, 'trips', tripId, 'seats', String(oldSeatNumber));
      await updateDoc(oldSeatRef, {
        status: 'AVAILABLE',
        bookingId: null,
        studentId: null,
        updatedAt: nowIso,
      });
    } catch (_) {}

    // Book new seat
    try {
      const newSeatRef = doc(db, 'trips', tripId, 'seats', String(newSeatNumber));
      await setDoc(
        newSeatRef,
        {
          seatNumber: newSeatNumber,
          tripId,
          status: 'BOOKED',
          bookingId,
          updatedAt: nowIso,
        },
        { merge: true }
      );
    } catch (_) {}

    // Update booking
    try {
      await updateDoc(doc(db, 'bookings', bookingId), {
        seatNumber: newSeatNumber,
        adminNote: `تم تغيير المقعد من #${oldSeatNumber} إلى #${newSeatNumber} بواسطة (${adminEmail || 'Admin'})`,
        updatedAt: nowIso,
      });
    } catch (_) {}

    // Update local dev store
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem('nashgo_dev_student_bookings');
        if (raw) {
          const list = JSON.parse(raw) as Booking[];
          const updated = list.map((b) => (b.id === bookingId ? { ...b, seatNumber: newSeatNumber } : b));
          sessionStorage.setItem('nashgo_dev_student_bookings', JSON.stringify(updated));
        }
      } catch (_) {}
    }

    return {
      success: true,
      message: `تم تغيير المقعد إلى #${newSeatNumber} بنجاح.`,
    };
  },

  /**
   * 8. Postpone booking (Frees seat, marks as POSTPONED, reserves credit)
   */
  async postponeBooking(input: AdminPostponeBookingInput): Promise<{ success: boolean; message: string }> {
    const { bookingId, tripId, seatNumber, adminEmail, postponeNotes } = input;
    const nowIso = new Date().toISOString();

    // 1. Release seat
    try {
      const seatRef = doc(db, 'trips', tripId, 'seats', String(seatNumber));
      await updateDoc(seatRef, {
        status: 'AVAILABLE',
        bookingId: null,
        studentId: null,
        updatedAt: nowIso,
      });
    } catch (_) {}

    // 2. Increment available seats on Trip (capped at totalSeats)
    try {
      const tripRef = doc(db, 'trips', tripId);
      const snap = await getDoc(tripRef);
      if (snap.exists()) {
        const cur = snap.data()?.availableSeats || 0;
        const maxCap = snap.data()?.totalSeats || snap.data()?.capacity || 50;
        const newAvail = Math.min(maxCap, cur + 1);
        await updateDoc(tripRef, {
          availableSeats: newAvail,
          availableSeatsCount: newAvail,
          status: 'OPEN',
          updatedAt: nowIso,
        });
      }
    } catch (_) {}

    // 3. Mark booking as POSTPONED
    const note = `تم تأجيل الرحلة بواسطة الإدارة (${adminEmail || 'Admin'}). المقعد مفرغ والرصيد محفوظ للطالب. ${postponeNotes ? `ملاحظة: ${postponeNotes}` : ''}`;
    try {
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'POSTPONED',
        bookingStatus: 'POSTPONED',
        adminNote: note,
        updatedAt: nowIso,
      });
    } catch (_) {}

    // Local dev store update
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem('nashgo_dev_student_bookings');
        if (raw) {
          const list = JSON.parse(raw) as Booking[];
          const updated = list.map((b) =>
            b.id === bookingId ? { ...b, status: 'POSTPONED' as BookingStatus, bookingStatus: 'POSTPONED' as BookingStatus, adminNote: note } : b
          );
          sessionStorage.setItem('nashgo_dev_student_bookings', JSON.stringify(updated));
        }
      } catch (_) {}
    }

    return {
      success: true,
      message: 'تم تأجيل الرحلة بنجاح، وتحرير المقعد مع الاحتفاظ بحق الطالب في الحجز لاحقاً.',
    };
  },

  /**
   * 9. Cancel booking
   */
  async cancelBooking(
    bookingId: string,
    tripId: string,
    seatNumber: number,
    adminEmail?: string,
    cancelReason?: string
  ): Promise<{ success: boolean; message: string }> {
    const nowIso = new Date().toISOString();

    // 1. Release seat in trips/{tripId}/seats/{seatNumber}
    try {
      const seatRef = doc(db, 'trips', tripId, 'seats', String(seatNumber));
      await setDoc(
        seatRef,
        {
          id: String(seatNumber),
          seatNumber,
          tripId,
          status: 'AVAILABLE',
          bookingId: null,
          studentId: null,
          updatedAt: nowIso,
        },
        { merge: true }
      );
    } catch (_) {}

    // 2. Remove any active seat locks for this seat
    try {
      const lockRef = doc(db, 'trips', tripId, 'seatLocks', String(seatNumber));
      await deleteDoc(lockRef);
    } catch (_) {}

    // 3. Increment available seats on Trip (capped at totalSeats)
    try {
      const tripRef = doc(db, 'trips', tripId);
      const snap = await getDoc(tripRef);
      if (snap.exists()) {
        const cur = snap.data()?.availableSeats || 0;
        const maxCap = snap.data()?.totalSeats || snap.data()?.capacity || 50;
        const newAvail = Math.min(maxCap, cur + 1);
        await updateDoc(tripRef, {
          availableSeats: newAvail,
          availableSeatsCount: newAvail,
          status: 'OPEN',
          updatedAt: nowIso,
        });
      }
    } catch (_) {}

    // 4. Update booking document
    const note = `تم إلغاء الحجز بواسطة مسؤول النظام (${adminEmail || 'Admin'}). ${cancelReason ? `السبب: ${cancelReason}` : ''}`;
    let parentGroupId: string | null = null;
    try {
      const bRef = doc(db, 'bookings', bookingId);
      const bSnap = await getDoc(bRef);
      if (bSnap.exists()) {
        parentGroupId = bSnap.data()?.bookingGroupId || null;
      }
      await setDoc(
        bRef,
        {
          status: 'CANCELLED',
          bookingStatus: 'CANCELLED',
          paymentStatus: 'CANCELLED',
          adminNote: note,
          updatedAt: nowIso,
        },
        { merge: true }
      );
    } catch (e) {
      console.warn('Booking cancel updateDoc error:', e);
    }

    // 5. If booking is part of a bookingGroup, update child item in bookingGroup doc
    if (parentGroupId) {
      try {
        const gRef = doc(db, 'bookingGroups', parentGroupId);
        const gSnap = await getDoc(gRef);
        if (gSnap.exists()) {
          const gData = gSnap.data();
          const updatedBookings = (gData?.bookings || []).map((item: any) =>
            item.bookingId === bookingId ? { ...item, status: 'CANCELLED' } : item
          );
          await setDoc(gRef, { bookings: updatedBookings, updatedAt: nowIso }, { merge: true });
        }
      } catch (gErr) {
        console.warn('BookingGroup update error in cancelBooking:', gErr);
      }
    }

    // 6. Sync localStorage and sessionStorage
    if (typeof window !== 'undefined') {
      try {
        const syncList = (raw: string | null) => {
          if (!raw) return '[]';
          const list = JSON.parse(raw) as Booking[];
          const updated = list.map((b) =>
            b.id === bookingId
              ? {
                  ...b,
                  status: 'CANCELLED' as BookingStatus,
                  bookingStatus: 'CANCELLED' as BookingStatus,
                  paymentStatus: 'CANCELLED' as PaymentStatus,
                  adminNote: note,
                }
              : b
          );
          return JSON.stringify(updated);
        };

        const updatedSession = syncList(sessionStorage.getItem('nashgo_dev_student_bookings'));
        const updatedLocal = syncList(localStorage.getItem('nashgo_dev_student_bookings'));
        sessionStorage.setItem('nashgo_dev_student_bookings', updatedSession);
        localStorage.setItem('nashgo_dev_student_bookings', updatedLocal);

        // Update single booking cache if present
        const bKey = `nashgo_dev_booking_${bookingId}`;
        const bRaw = sessionStorage.getItem(bKey) || localStorage.getItem(bKey);
        if (bRaw) {
          const bObj = JSON.parse(bRaw);
          bObj.status = 'CANCELLED';
          bObj.bookingStatus = 'CANCELLED';
          bObj.paymentStatus = 'CANCELLED';
          bObj.adminNote = note;
          sessionStorage.setItem(bKey, JSON.stringify(bObj));
          localStorage.setItem(bKey, JSON.stringify(bObj));
        }
      } catch (_) {}
    }

    // 7. Audit log
    await auditService.logAction(
      adminEmail || 'admin',
      adminEmail || 'admin@otostudents.com',
      'TRIP_UPDATED' as any,
      'TRIP' as any,
      bookingId,
      {
        action: 'CANCEL_BOOKING',
        tripId,
        seatNumber,
        cancelReason: cancelReason || 'إلغاء من منافيست الرحلة',
      }
    ).catch(() => {});

    return {
      success: true,
      message: `تم إلغاء حجز المقعد #${seatNumber} بنجاح وتحريره للرحلة.`,
    };
  },

  /**
   * 10. Helper: generate formatted WhatsApp URL for student
   */
  getWhatsAppUrl(phoneNumber?: string, messageText?: string): string {
    if (!phoneNumber) return '#';
    let clean = phoneNumber.replace(/[^0-9]/g, '');
    if (clean.startsWith('0')) {
      clean = '2' + clean;
    }
    const encoded = encodeURIComponent(messageText || '');
    return `https://wa.me/${clean}?text=${encoded}`;
  },

  /**
   * 11. Helper: generate concise official welcome WhatsApp message with booking & payment steps
   */
  getStudentWelcomeWhatsAppMessage(student: {
    fullName: string;
    phoneNumber?: string;
    universityName?: string;
    assignedRoute?: string;
    defaultPickupStation?: string;
  }): string {
    const name = student.fullName?.trim() || 'طالبنا العزيز';

    return `السلام عليكم يا ${name} 👋
أهلاً ومرحباً بك في أوتو ستيودنتس (OTO Students) 🚌🎓
إحدى خدمات شركة أوتوبيسي للنقل الذكي.

سعداء بانضمامك لمنظومتنا المخصصة لنقل طلاب الجامعات من وإلى الإسكندرية!

━━━━━━━━━━━━━━━━━━━━━
📢 تنويه وملاحظات هامة بخصوص الرحلات والأسعار:

1️⃣ موعد انطلاق أولى الرحلات:
🚀 تنطلق أولى رحلاتنا الرسمية بإذن الله يوم الأحد 1 نوفمبر، وباب الحجز مفتوح ومتاح لك من الآن لاختيار مقعدك عبر المنصة!

2️⃣ نظام التسعير التوفيري وضمان استرداد الفارق:
• تبدأ الرحلات بسيارات تويوتا هاي إس (HiAce) مكيفة حديثة موديل 2026 سعة 13 راكباً.
• عند وصول عدد الطلاب الحاجزين بالرحلة إلى 33 طالباً، يتم فوراً ترقية الرحلة إلى ميني باص سياحي سعة 33 راكباً بسعر تذكرة أقل ومخفض.
• وفي حالة وصول عدد الحاجزين إلى 50 طالباً، يتم استخدام حافلة سياحية كبرى سعة 50 راكباً بسعر تذكرة أوفر بكثير!
✨ إذا قمت بالحجز مسبقاً بالسعر الأولي وتمت ترقية المركبة وتخفيض السعر، سيتم استرداد فارق السعر لك بالكامل فوراً ودون أي خصم!

━━━━━━━━━━━━━━━━━━━━━
📲 خطوات حجز رحلتك ومقعدك بكل سهولة:
1️⃣ ادخل على حسابك بالمنصة:
🌐 https://otostudents.com
2️⃣ اختر موعد رحلتك ومقعدك:
حدد جامعتك وتاريخ الرحلة (ذهاب / عودة / باقة أسبوعية)، واختر مقعدك المفضل على مخطط المقاعد 💺
3️⃣ سداد التذكرة وتأكيد الحجز 💳:
ادفع بسهولة عبر الوسيلة الأنسب لك:
• انستاباي (InstaPay)
• المحافظ الإلكترونية (فودافون / أورنج / اتصالات / وي كاش)
📲 فور التأكيد ستظهر تذكرتك الإلكترونية في حسابك متضمنة رقم المقعد وبيانات الحافلة وموعد ومحطة التحرك.

إذا واجهتك أي صعوبة أو لديك استفسار، تواصل معنا عبر هذه المحادثة مباشرة 💬
— فريق خدمة عملاء أوتو ستيودنتس | شركة أوتوبيسي للنقل الذكي
📞 للدعم والمساعدة: 01207337330`;
  },

  /**
   * 12. Helper: generate full official welcome WhatsApp URL for student
   */
  getStudentWelcomeWhatsAppUrl(student: {
    fullName: string;
    phoneNumber?: string;
    universityName?: string;
    assignedRoute?: string;
    defaultPickupStation?: string;
  }): string {
    if (!student.phoneNumber) return '#';
    const message = this.getStudentWelcomeWhatsAppMessage(student);
    return this.getWhatsAppUrl(student.phoneNumber, message);
  },
};
